/**
 * MetaAdsLocationDevices — página "Localização / Dispositivos" do Meta Ads.
 *
 * Espelha o layout da página homônima do Google Ads (LocationDevices.jsx):
 * grid de KPIs no topo, barra de métrica ativa e o mesmo grid principal
 * (mapa + breakdowns 2x2), reaproveitando as classes visuais (kpi-*, bw-*,
 * seo-*). Diferenças: origem dos dados (/api/meta-ads/*) e ausência de CRM —
 * o mapa geográfico da Meta não expõe coordenadas, então usa o mesmo aviso
 * "dados por região indisponíveis" que o Google já exibe nesse caso, e os
 * cards de breakdown trazem as dimensões da Meta.
 */

import { useMemo, useState } from 'react'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import LeadsMap, { NAME_TO_UF } from '../../components/LeadsMap.jsx'
import { useApp } from '../../context/AppContext.jsx'
import {
  getMetaAdsSummary, getMetaAdsBreakdowns,
} from '../../db/api.js'
import { useApiData } from '../../hooks/useApiData.js'
import MetaAccountFilter, { useMetaAccountFilter } from '../../components/MetaAccountFilter.jsx'
import KpiCard from '../../components/KpiCard.jsx'
import PageState from '../../components/PageState.jsx'
import { fmtBRL, fmtNum } from '../../lib/format.js'
import './metaPages.css'

// Métrica ativa (equivale ao MetricFilterBar do Google): decide o valor que
// alimenta as barras dos breakdowns + o rótulo absoluto.
const METRICS = [
  { key: 'impressions', label: 'Impressões', fmt: fmtNum, unit: 'impr.' },
  { key: 'clicks', label: 'Cliques', fmt: fmtNum, unit: 'cliques' },
  { key: 'spend', label: 'Investimento', fmt: fmtBRL, unit: '' },
  { key: 'reach', label: 'Alcance', fmt: fmtNum, unit: 'pessoas' },
]

// ─────────────────────────────────────────────────────────────────────────────
// Blocos visuais (mesmas classes do Google: kpi-card, bw-panel, bw-bar-*)
// ─────────────────────────────────────────────────────────────────────────────

function BarRow({ name, percentage, value, unit }) {
  return (
    <div className="bw-bar-row">
      <div className="bw-bar-meta">
        <span className="bw-bar-name" title={name}>{name}</span>
        <span className="bw-bar-pct">
          {percentage}% <span className="bw-bar-abs">({value}{unit ? ` ${unit}` : ''})</span>
        </span>
      </div>
      <div className="bw-bar-track">
        <div className="bw-bar-fill" style={{ width: `${Math.min(100, percentage)}%` }} />
      </div>
    </div>
  )
}

function BreakdownCard({ icon, title, rows, metric }) {
  return (
    <div className="bw-panel seo-card">
      <div className="bw-panel-head">
        <Icon n={icon} s={14} />
        <span>{title}</span>
      </div>
      <div className="bw-panel-body">
        {rows.length === 0
          ? <div className="bw-panel-empty">Sem dados no período</div>
          : rows.map((r) => (
              <BarRow
                key={r.name}
                name={r.name}
                percentage={r.percentage}
                value={metric.fmt(r.value)}
                unit={metric.unit}
              />
            ))}
      </div>
    </div>
  )
}

// Região da Meta ("São Paulo (state)", "Federal District", "Unknown") → UF.
// O choropleth pinta por UF, então basta a sigla — não precisa de coordenadas.
const REGION_ALIAS = {
  'Federal District': 'Distrito Federal',
}

function regionToUF(region) {
  if (!region) return null
  let n = String(region).replace(/\s*\(state\)\s*$/i, '').trim()
  n = REGION_ALIAS[n] || n
  return NAME_TO_UF[n] || null
}

// location breakdown → shape do LeadsMap ([{state, leads}]) + meta (unplaced).
function buildMapData(location, metricKey) {
  const byUF = new Map()
  let total = 0
  let unplaced = 0
  for (const r of location || []) {
    if (r.country && String(r.country).toUpperCase() !== 'BR') { continue }
    const v = Number(r[metricKey] || 0)
    total += v
    const uf = regionToUF(r.region)
    if (!uf) { unplaced += v; continue }
    byUF.set(uf, (byUF.get(uf) || 0) + v)
  }
  const data = [...byUF.entries()].map(([state, leads]) => ({ state, leads, topCities: [] }))
  return { data, meta: { total, unplaced, noLocation: unplaced, unrecognized: 0 } }
}

// Agrega linhas de breakdown por uma dimensão (soma da métrica ativa) e
// devolve top-N já com percentual sobre o total.
function aggregate(rows, dimKey, metricKey, topN = 8) {
  const map = new Map()
  let total = 0
  for (const r of rows || []) {
    const name = r[dimKey] || '—'
    const v = Number(r[metricKey] || 0)
    map.set(name, (map.get(name) || 0) + v)
    total += v
  }
  const out = [...map.entries()]
    .map(([name, value]) => ({
      name,
      value,
      percentage: total > 0 ? Math.round((value / total) * 100) : 0,
    }))
    .sort((a, b) => b.value - a.value)
  return out.slice(0, topN)
}

export default function MetaAdsLocationDevices() {
  const { period } = useApp()
  const { start, end } = period || {}
  const { accounts, accountIds, setAccountIds } = useMetaAccountFilter()
  const accountKey = accountIds.join(',')
  const [metricKey, setMetricKey] = useState('impressions')
  const metric = METRICS.find(m => m.key === metricKey) || METRICS[0]

  const { data: summary, loading: sumLoading, error: sumError, reload: reloadSummary } = useApiData(
    () => getMetaAdsSummary(start, end, accountIds),
    [start, end, accountKey],
    { initialData: { totals: {}, series: [] } }
  )
  const { data: devices, loading: devLoading } = useApiData(
    () => getMetaAdsBreakdowns('devices', start, end, accountIds),
    [start, end, accountKey],
    { initialData: [] }
  )
  const { data: location, loading: locLoading } = useApiData(
    () => getMetaAdsBreakdowns('location', start, end, accountIds),
    [start, end, accountKey],
    { initialData: [] }
  )
  const { data: demographics, loading: demoLoading } = useApiData(
    () => getMetaAdsBreakdowns('demographics', start, end, accountIds),
    [start, end, accountKey],
    { initialData: [] }
  )

  const loading = sumLoading || devLoading || locLoading || demoLoading
  const error = sumError && String(sumError)
  const t = summary?.totals || {}

  const platformRows = useMemo(() => aggregate(devices, 'publisher_platform', metricKey), [devices, metricKey])
  const deviceRows = useMemo(() => aggregate(devices, 'device_platform', metricKey), [devices, metricKey])
  const regionRows = useMemo(() => aggregate(location, 'region', metricKey), [location, metricKey])
  const ageRows = useMemo(() => aggregate(demographics, 'age', metricKey), [demographics, metricKey])
  const { data: mapData, meta: mapMeta } = useMemo(() => buildMapData(location, metricKey), [location, metricKey])

  const kpis = [
    { icon: 'currency', label: 'Investimento', value: fmtBRL(t.spend), hint: 'Total investido no período' },
    { icon: 'eye', label: 'Impressões', value: fmtNum(t.impressions), hint: 'Total de impressões' },
    { icon: 'target', label: 'Cliques', value: fmtNum(t.clicks), hint: 'Total de cliques' },
    { icon: 'users', label: 'Alcance', value: fmtNum(t.reach), hint: 'Pessoas alcançadas' },
  ]

  return (
    <Layout
      title="Meta Ads — Localização / Dispositivos"
      breadcrumb={[{ label: 'Marketing' }, { label: 'Meta Ads', to: '/meta-ads' }, { label: 'Localização / Dispositivos' }]}
      showPeriod
      topbarRight={<MetaAccountFilter accounts={accounts} selected={accountIds} onChange={setAccountIds} />}
    >
      <div className="dash-shell">
        <PageState loading={loading} error={error} onRetry={reloadSummary}>
          {/* KPIs */}
          <div className="kpi-grid kpi-grid-4">
            {kpis.map((k) => <KpiCard key={k.label} {...k} />)}
          </div>

          {/* Barra de métrica ativa (equivale ao MetricFilterBar do Google) */}
          <div className="metric-filter-bar" role="tablist" aria-label="Filtrar widgets por métrica">
            {METRICS.map((m) => {
              const isActive = metricKey === m.key
              return (
                <button
                  key={m.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`metric-filter-btn ${isActive ? 'is-active' : ''}`}
                  onClick={() => setMetricKey(m.key)}
                >
                  <span>{m.label}</span>
                </button>
              )
            })}
          </div>

          {/* Grid principal: mapa + breakdowns 2x2 (mesmo layout do Google). */}
          <div className="seo-main-grid meta-locdev-grid">
            <LeadsMap data={mapData} metricLabel={metric.label.toLowerCase()} meta={mapMeta} />
            <div className="seo-breakdowns-grid">
              <BreakdownCard icon="link" title="Plataformas" rows={platformRows} metric={metric} />
              <BreakdownCard icon="device" title="Dispositivos" rows={deviceRows} metric={metric} />
              <BreakdownCard icon="geo" title="Localização" rows={regionRows} metric={metric} />
              <BreakdownCard icon="users" title="Demografia (idade)" rows={ageRows} metric={metric} />
            </div>
          </div>
        </PageState>
      </div>
    </Layout>
  )
}
